import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { API, StarInput } from './stars';

const Thumb = ({ photo, onRemove }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    axios.get(`${API}/five-star/photo`, { params: { path: photo.path }, responseType: 'blob' }).then(r => setUrl(URL.createObjectURL(r.data))).catch(() => setUrl(''));
  }, [photo.path]);
  return (
    <div className="relative h-16 w-20 overflow-hidden rounded-md border bg-slate-100">
      {url && <img src={url} alt={photo.name} className="h-full w-full object-cover" />}
      <button type="button" onClick={onRemove} className="absolute right-1 top-1 rounded-full bg-white/90 p-0.5 text-rose-600" title="Kaldır"><X className="h-3 w-3" /></button>
    </div>
  );
};

// Not + resim düzenleyici (personel satırı ve başlık altı için ortak)
const NoteEditor = ({ note, onChange, placeholder, testId, compact }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    try {
      const added = [];
      for (const f of files) { const fd = new FormData(); fd.append('file', f); added.push((await axios.post(`${API}/five-star/photos`, fd)).data); }
      onChange({ ...note, photos: [...note.photos, ...added].slice(0, 8) });
    } catch (err) { toast.error(err.response?.data?.detail || 'Resim yüklenemedi'); } finally { setBusy(false); }
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? 'justify-center' : ''}`}>
      <Input value={note.comment} onChange={ev => onChange({ ...note, comment: ev.target.value })} placeholder={placeholder} className={`h-8 bg-white text-xs ${compact ? 'w-full' : 'max-w-xl flex-1'}`} data-testid={testId} />
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple hidden onChange={ev => { upload([...ev.target.files]); ev.target.value = ''; }} />
      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={busy || note.photos.length >= 8} onClick={() => ref.current?.click()} data-testid={`${testId}-photo`}>
        {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="mr-1 h-3.5 w-3.5" />}Resim{note.photos.length ? ` (${note.photos.length})` : ''}
      </Button>
      {note.photos.map((p, i) => <Thumb key={p.path} photo={p} onRemove={() => onChange({ ...note, photos: note.photos.filter((_, k) => k !== i) })} />)}
    </div>
  );
};

const Prev = ({ prev, cur }) => {
  if (prev == null) return null;
  const d = cur ? cur - prev : 0;
  return (
    <span className={`text-[10px] ${!cur ? 'text-slate-400' : d > 0 ? 'text-emerald-600' : d < 0 ? 'text-rose-600' : 'text-slate-500'}`}>
      Önceki: {prev}{cur ? (d > 0 ? ' ▲' : d < 0 ? ' ▼' : ' =') : ''}
    </span>
  );
};

// Satır: personel, sütun: denetim başlıkları; her hücre ayrı yıldız
export const AuditMatrix = ({ criteria, employees, items, setItem, previous }) => {
  const pc = previous?.criteria || {};
  const pe = previous?.employees || {};
  const pn = previous?.notes || {};
  const low = previous?.threshold ?? 2.5;
  return (
    <div className="overflow-x-auto rounded-xl border bg-white" data-testid="audit-matrix">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b bg-slate-50">
            <th className="sticky left-0 z-10 min-w-[180px] bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700">Personel</th>
            {criteria.map(c => {
              const it = items[c.id];
              return (
                <th key={c.id} className={`min-w-[150px] border-l px-2 py-2 text-center align-top ${it.applicable ? '' : 'bg-slate-100 opacity-60'}`} data-testid={`criterion-col-${c.id}`}>
                  <p className="font-semibold text-slate-900" title={c.description}>{c.name}{(c.weight ?? 1) !== 1 && <span className="ml-1 rounded bg-amber-100 px-1.5 text-[10px] text-amber-800" data-testid={`criterion-weight-${c.id}`}>x{c.weight}</span>}</p>
                  {pc[c.id] != null && <p className="text-[11px] font-normal text-slate-500" data-testid={`criterion-prev-${c.id}`}>Önceki denetim: <b>{pc[c.id]}</b></p>}
                  <label className="mt-1 flex items-center justify-center gap-1.5 text-[11px] font-normal text-slate-500">Uygulanmaz<Switch className="scale-75" checked={!it.applicable} onCheckedChange={v => setItem(c.id, { ...it, applicable: !v })} data-testid={`criterion-na-${c.id}`} /></label>
                  {it.applicable && employees.length > 1 && (
                    <div className="mt-1 flex justify-center gap-1 text-[10px] font-normal text-slate-400">Hepsi:
                      {[1, 2, 3, 4, 5].map(n => <button key={n} type="button" onClick={() => setItem(c.id, { ...it, scores: Object.fromEntries(employees.map(e => [e.id, n])) })} className="rounded border px-1.5 hover:bg-white" data-testid={`star-all-${c.id}-${n}`}>{n}</button>)}
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {employees.map(e => [
            <tr key={e.id} className="hover:bg-amber-50/30" data-testid={`audit-row-${e.id}`}>
              <td rowSpan={2} className="sticky left-0 z-10 border-b bg-white px-4 py-3 align-top">
                <p className="font-semibold text-slate-900">{e.name}</p>
                <p className="text-xs text-slate-500">{e.task || e.position || '-'} · <span className="font-mono">{e.tc}</span></p>
              </td>
              {criteria.map(c => {
                const it = items[c.id];
                const lastNote = pn[e.id]?.[c.id];
                return (
                  <td key={c.id} className={`border-l px-2 pt-2 text-center align-top ${!it.applicable ? 'bg-slate-100' : it.scores[e.id] ? '' : 'bg-amber-50/60'}`}>
                    {it.applicable ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <StarInput compact value={it.scores[e.id]} onChange={v => setItem(c.id, { ...it, scores: { ...it.scores, [e.id]: v } })} testId={`star-${c.id}-${e.id}`} />
                        <Prev prev={pe[e.id]?.[c.id]} cur={it.scores[e.id]} />
                        {it.scores[e.id] && it.scores[e.id] < low && pe[e.id]?.[c.id] != null && pe[e.id][c.id] < low && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white" title="Bir önceki denetimde de düşük puan almıştı" data-testid={`repeat-${c.id}-${e.id}`}>Tekrarlıyor</span>}
                        {lastNote && <p className="line-clamp-2 max-w-[200px] text-[10px] italic text-slate-500" title={lastNote} data-testid={`prev-note-${c.id}-${e.id}`}>Son not: {lastNote}</p>}
                      </div>
                    ) : <span className="text-xs text-slate-400">Uygulanmaz</span>}
                  </td>
                );
              })}
            </tr>,
            <tr key={`${e.id}-n`} className="border-b">
              {criteria.map(c => {
                const it = items[c.id];
                const n = it.notes?.[e.id] || { comment: '', photos: [] };
                return (
                  <td key={c.id} className={`border-l px-2 pb-2 pt-1 align-top ${!it.applicable ? 'bg-slate-100' : ''}`}>
                    {it.applicable && <NoteEditor compact note={n} onChange={v => setItem(c.id, { ...it, notes: { ...(it.notes || {}), [e.id]: v } })} placeholder="Not (isteğe bağlı)" testId={`cell-note-${c.id}-${e.id}`} />}
                  </td>
                );
              })}
            </tr>,
          ])}
        </tbody>
      </table>
    </div>
  );
};
